import { MarkdownSerializer } from 'prosemirror-markdown';
import {
  MessageMarkdownTransformer,
  messageSchema,
} from '@chatwoot/prosemirror-schema';
import { comoLinhas } from './blankLines';
import {
  mention,
  blockquote,
  code_block as codeBlock,
  bullet_list as bulletList,
  ordered_list as orderedList,
  list_item as listItem,
  paragraph,
  image,
  text,
  link,
  code,
} from '@chatwoot/prosemirror-schema/src/schema/markdown/serializer';

const ALFANUMERICO = /[\p{L}\p{N}]/u;

/**
 * Os caracteres colados nas duas pontas de uma faixa marcada.
 *
 * O `index` que o serializador entrega aponta para o no que esta sendo aberto
 * ou para o primeiro no ja sem a marca, no fechamento. Como a faixa pode estar
 * quebrada em varios nos (por outra marca no meio, por exemplo), ela e expandida
 * para os dois lados antes de olhar os vizinhos — assim abertura e fechamento
 * enxergam o mesmo contexto e escolhem o mesmo delimitador.
 *
 * @param {Mark} mark
 * @param {Node} parent
 * @param {number} index
 * @param {boolean} abrindo
 * @returns {{antes: string, depois: string}}
 */
function vizinhosDaFaixa(mark, parent, index, abrindo) {
  let inicio = abrindo ? index : index - 1;
  let fim = inicio;

  while (inicio > 0 && mark.isInSet(parent.child(inicio - 1).marks)) {
    inicio -= 1;
  }

  while (
    fim + 1 < parent.childCount &&
    mark.isInSet(parent.child(fim + 1).marks)
  ) {
    fim += 1;
  }

  // A letra do vizinho so encosta no delimitador quando nada e escrito entre
  // os dois. E o que a pergunta abaixo responde: o vizinho tem alguma marca
  // que a ponta da faixa nao tem?
  //
  // Se tem, essa marca fecha ali — `**neg**_ital_` poe o `**` entre a letra e
  // o `_`, e o mesmo vale para a crase do codigo e o `)` do link. Delimitador
  // e pontuacao, e pontuacao ja e fronteira valida para o `_`; ler a letra que
  // vem atras dela fazia sair `**neg***ital*`, com o italico em negrito.
  //
  // Se nao tem, nada e fechado entre eles e as letras se tocam de fato. Aqui
  // entra o caso inverso, que a comparacao por conjuntos IGUAIS lia errado: com
  // a marca extra na propria faixa (`[a(em), b(em,strong)]` seguido de texto
  // solto), o `_` do italico e escrito por FORA do `**` e encosta direto na
  // letra — saia `_a**b**_c`, sem enfase nenhuma no reparse.
  //
  // Cada ponta usa o no daquela ponta: a marca extra pode estar so num pedaco
  // da faixa, e julgar o fim pelas marcas do comeco erra do mesmo jeito.
  const colado = (no, ponta) =>
    no && no.isText && no.marks.every(m => m.isInSet(ponta.marks))
      ? no.text
      : '';

  return {
    antes:
      inicio > 0
        ? colado(parent.child(inicio - 1), parent.child(inicio)).slice(-1)
        : '',
    depois:
      fim + 1 < parent.childCount
        ? colado(parent.child(fim + 1), parent.child(fim)).slice(0, 1)
        : '',
  };
}

/**
 * Delimitador de italico que sobrevive ao round-trip.
 *
 * `_` e a escolha certa no caso comum (ver o comentario em `em`, abaixo), mas o
 * CommonMark so o trata como enfase quando ha fronteira de palavra dos dois
 * lados. Italico aplicado a um pedaco de palavra saia como `_abc_def`, e o parse
 * de volta nao achava enfase nenhuma: a marca sumia e o registro passava a
 * exibir os underlines crus, na tela e na mensagem entregue.
 *
 * Colado a alfanumerico, entao, cai para `*`. O `swapEmStrongPlugin` vai exibir
 * esse trecho em negrito — formatacao errada, mas preservada, que e melhor do
 * que perder a marca e ainda mostrar o delimitador como texto.
 *
 * LIMITE CONHECIDO: quando a PONTA da faixa de italico carrega `code` ou
 * `strike`, o delimitador dessa outra marca (crase ou `~~`) e escrito por
 * dentro do `*`. O asterisco fica entre pontuacao e letra, e ali o CommonMark
 * nao considera fecho de enfase — nem `*` nem `_` resolvem, entao a marca se
 * perde no reparse e o usuario ve os delimitadores crus:
 *
 *     [a(em,code)] + y            ->  *`a`*y
 *     y + [a(em,code)]            ->  y*`a`*
 *     [a(em), b(em,code)] + c     ->  *a`b`*c
 *     [a(em,strike)] + y          ->  *~~a~~*y
 *     [a(em,strong,strike)] + y   ->  ***~~a~~***y
 *
 * So acontece colado a alfanumerico: com espaco ou pontuacao ao lado, todos
 * sobrevivem. `strong` sozinho (`***a***y`) e `link` (`[*a*](url)y`) tambem
 * estao fora — no primeiro o `*` encosta em letra, no segundo em `]`.
 *
 * A unica alavanca que resta nao e a escolha do delimitador e sim a ORDEM em
 * que as marcas sao serializadas: com `~~`/crase por fora, o `*` fecharia
 * contra a letra e voltaria a ser right-flanking. Isso mexe na ordem das
 * marcas do schema e fica para tarefa separada.
 *
 * @param {Mark} mark
 * @param {Node} parent
 * @param {number} index
 * @param {boolean} abrindo
 * @returns {string}
 */
function delimitadorItalico(mark, parent, index, abrindo) {
  const { antes, depois } = vizinhosDaFaixa(mark, parent, index, abrindo);

  return ALFANUMERICO.test(antes) || ALFANUMERICO.test(depois) ? '*' : '_';
}

/**
 * Serializador canonico do editor.
 *
 * O pacote `@chatwoot/prosemirror-schema` exporta um `MessageMarkdownSerializer`
 * como singleton de modulo, e o editor antigo mutava esse singleton em tempo de
 * execucao para cuspir sintaxe do WhatsApp. Como a mutacao era global e
 * acontecia no `created()` de um componente, o formato gravado passava a
 * depender de qual tela o usuario tinha aberto antes.
 *
 * Aqui montamos uma instancia propria, com os delimitadores declarados
 * literalmente, para que a saida seja sempre CommonMark independentemente do
 * que qualquer outro modulo faca com o singleton do pacote.
 */
/**
 * A quebra de linha, escrita com barra invertida.
 *
 * O CommonMark tem duas formas para ela: dois espacos no fim da linha, ou uma
 * barra invertida. As duas sao lidas igual, mas so a barra sobrevive quando a
 * linha nao tem mais nada — uma linha com dois espacos e considerada VAZIA e
 * fecha o paragrafo, entao `a  \n  \nb` volta como dois paragrafos e a linha
 * em branco do meio se perde.
 *
 * Com a barra, `a\\\n\\\nb` volta inteiro, e e o que permite guardar quantas
 * linhas em branco o autor escreveu. Era a unica coisa que faltava: o WhatsApp
 * guarda a mensagem como uma string de texto onde `\n` e conteudo, e a
 * aplicacao guardava um formato de documento onde linha em branco e estrutura.
 *
 * Ler continua compativel: o conteudo antigo, gravado com dois espacos, e
 * reconhecido como quebra do mesmo jeito. A barra vale do que for gravado
 * daqui em diante.
 *
 * Quem entrega a mensagem tira a barra — ver `normalizeBlockBreaks`.
 *
 * @param {MarkdownSerializerState} state
 * @param {Node} node
 * @param {Node} parent
 * @param {number} index
 */
function hardBreakComBarra(state, node, parent, index) {
  // Quebra no fim do bloco nao vira nada: nao ha linha depois dela para
  // separar, e a barra sobraria no texto. Mesma regra do serializador do
  // pacote, que e de onde este veio.
  for (let i = index + 1; i < parent.childCount; i += 1) {
    if (parent.child(i).type !== node.type) {
      state.write('\\\n');
      return;
    }
  }
}

export const canonicalSerializer = new MarkdownSerializer(
  {
    mention,
    blockquote,
    code_block: codeBlock,
    bullet_list: bulletList,
    ordered_list: orderedList,
    list_item: listItem,
    paragraph,
    image,
    hard_break: hardBreakComBarra,
    text,
  },
  {
    // Italico sai como `_x_`, nao `*x*`, por dois motivos.
    //
    // O MessageFormatter registra `swapEmStrongPlugin`, que renderiza todo
    // `*x*` como <strong> — regra que existe para exibir corretamente as
    // mensagens que CHEGAM do WhatsApp, onde `*x*` e negrito. Gravar italico
    // como `*x*` faria o italico do agente aparecer em negrito na tela.
    //
    // E o CommonMark nao trata `_` no meio de palavra como enfase, entao
    // `arquivo_com_underline.txt` sobrevive ao round-trip — o que nao vale
    // para `*`, que enfatiza mesmo dentro de palavra.
    //
    // A excecao e a borda de palavra, onde `_` nao delimita enfase nenhuma —
    // ver `delimitadorItalico`.
    em: {
      open: (state, mark, parent, index) =>
        delimitadorItalico(mark, parent, index, true),
      close: (state, mark, parent, index) =>
        delimitadorItalico(mark, parent, index, false),
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    strong: {
      open: '**',
      close: '**',
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    strike: {
      open: '~~',
      close: '~~',
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    link,
    code,
  }
);

/**
 * Converte o documento do editor em CommonMark.
 *
 * @param {Node} doc documento ProseMirror
 * @returns {string}
 */
export function serialize(doc) {
  return canonicalSerializer.serialize(doc);
}

/**
 * Converte CommonMark em documento do editor.
 *
 * Repare que o conteudo NAO passa por nenhuma normalizacao de sintaxe do
 * WhatsApp. O editor antigo aplicava `normalizeWhatsAppMarkdown` aqui, o que
 * reescrevia `*x*` como `**x**` toda vez que a tela abria — ou seja, abrir um
 * registro para conferir ja alterava o que seria salvo. O formato guardado e
 * CommonMark e e lido como CommonMark.
 *
 * @param {string} content markdown
 * @returns {Node} documento ProseMirror
 */
export function parse(content = '') {
  const doc = new MessageMarkdownTransformer(messageSchema).parse(
    content || ''
  );

  // O texto corrido vira LINHAS aqui: o markdown ainda traz paragrafo, e no
  // editor a linha e `hard_break`. Sem isto o Enter misturaria as duas
  // representacoes e a linha em branco voltaria a ser incontavel.
  return comoLinhas(doc, messageSchema);
}

export { messageSchema };
